/**
 * Browser download helpers.
 *
 * Every "save this file" path goes through here so the object-URL lifecycle,
 * the Content-Disposition parsing and the CSV byte-order mark cannot drift
 * apart between call sites — before this module the same ~10 lines were copied
 * into two dozen files, each with its own small differences.
 *
 * Not for image previews: `URL.createObjectURL` on a picked file that stays on
 * screen has a different lifecycle (revoke on unmount, not after a click).
 */

/** Anchor-click download. Kept private so callers can't skip the cleanup. */
function clickDownload(url: string, filename: string, newTab = false): void {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  if (newTab) a.target = "_blank";
  document.body.appendChild(a);
  try {
    a.click();
  } finally {
    // A blocked-popup exception must not leave the anchor in the document.
    a.remove();
  }
}

/**
 * Download a file that is already hosted somewhere — a presigned S3 link, a
 * signed media URL. Nothing to revoke: the URL is not ours.
 */
export function triggerBrowserDownload(
  url: string,
  filename: string,
  opts: { newTab?: boolean } = {},
): void {
  clickDownload(url, filename, opts.newTab ?? true);
}

/** Save an in-memory blob, always releasing the object URL afterwards. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  try {
    clickDownload(url, filename);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Save CSV text. The UTF-8 BOM is what makes Excel read "ł", "ą", "ę" instead
 * of mojibake — it is the whole reason CSV gets its own helper.
 */
export function downloadCsv(csv: string, filename: string): void {
  const name = filename.toLowerCase().endsWith(".csv") ? filename : `${filename}.csv`;
  downloadBlob(new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" }), name);
}

/** Quote one CSV cell: doubles inner quotes, wraps everything. */
export function csvCell(value: unknown): string {
  return `"${String(value ?? "").replace(/"/g, '""')}"`;
}

/** Rows of cells → CSV text (no BOM; `downloadCsv` adds it). */
export function toCsv(rows: unknown[][]): string {
  return rows.map((r) => r.map(csvCell).join(",")).join("\n");
}

/** Types the browser renders natively that aren't covered by a prefix check. */
const VIEWABLE_EXACT = new Set([
  "application/pdf",
  "application/json",
  "text/plain",
]);

/**
 * A blob: URL inherits the app's origin, so opening one of these as a
 * top-level document would run scripts from a user-picked file with access to
 * our storage (auth tokens included). They stay download-only.
 */
const SCRIPTABLE = new Set(["text/html", "application/xhtml+xml", "image/svg+xml"]);

/** Fallback for files the OS reports with an empty MIME type. */
const EXTENSION_MIME: Record<string, string> = {
  pdf: "application/pdf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  bmp: "image/bmp",
  txt: "text/plain",
  log: "text/plain",
  json: "application/json",
  mp4: "video/mp4",
  webm: "video/webm",
  mp3: "audio/mpeg",
  wav: "audio/wav",
};

function viewableMimeType(file: File): string | null {
  let type = file.type;
  if (!type) {
    const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
    type = EXTENSION_MIME[ext] ?? "";
  }
  if (!type || SCRIPTABLE.has(type)) return null;
  if (VIEWABLE_EXACT.has(type)) return type;
  if (["image/", "video/", "audio/"].some((p) => type.startsWith(p))) return type;
  return null;
}

/**
 * Preview a not-yet-uploaded file from a form's attachment list: open it in a
 * new tab when the browser can render its type, otherwise download it. The
 * object URL lives only for the click (plus the grace period the new tab
 * needs to load), so this stays within this module's lifecycle rules.
 */
export function openPendingFile(file: File): void {
  const viewType = viewableMimeType(file);
  if (!viewType) {
    downloadBlob(file, file.name);
    return;
  }
  // Files with an empty type get re-wrapped so the tab renders them instead
  // of treating the blob as an opaque download.
  const blob = file.type ? file : new Blob([file], { type: viewType });
  const url = URL.createObjectURL(blob);
  // No "noopener" here: it makes window.open return null, which is our only
  // popup-blocked signal, and the viewable types above can't run scripts.
  const win = window.open(url, "_blank");
  if (!win) {
    // Popup blocked — degrade to a download rather than doing nothing.
    URL.revokeObjectURL(url);
    downloadBlob(file, file.name);
    return;
  }
  // The new tab needs the URL alive until it finishes loading.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

interface BlobResponseLike {
  data: unknown;
  headers?: Record<string, unknown>;
}

/** Filename from Content-Disposition, falling back when the header is absent. */
export function filenameFromResponse(
  resp: BlobResponseLike,
  fallback: string,
): string {
  const cd = (resp.headers?.["content-disposition"] as string) || "";
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
  if (!match) return fallback;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

/** Save an axios blob response under the filename the server asked for. */
export function saveBlobResponse(
  resp: BlobResponseLike,
  fallbackName: string,
): void {
  downloadBlob(resp.data as Blob, filenameFromResponse(resp, fallbackName));
}
